<template>
  <div class="excel-like-table">
    <!-- 顶部工具栏：筛选栏、总营业额和导出按钮 -->
    <div class="table-header">
      <!-- 筛选栏：日期和场地选择器 -->
      <div class="filter-bar">
        <el-date-picker
          v-model="selectedDate"
          type="date"
          placeholder="选择日期"
          value-format="yyyy-MM-dd"
          @change="handleDateChange"
        ></el-date-picker>
        
        <el-select
          v-model="selectedGroundId"
          placeholder="选择场地"
          @change="handleGroundChange"
          style="width: 200px; margin-left: 10px"
        >
          <el-option
            v-for="ground in grounds"
            :key="ground.groundId"
            :label="ground.groundName"
            :value="ground.groundId.toString()"
          ></el-option>
        </el-select>
      </div>
      
      <!-- 总营业额显示 -->
      <div class="turnover-display">
        <span>总营业额</span>
        <span v-if="turnoverError" class="text-danger">获取失败</span>
        <span v-else>¥ {{ turnoverData !== null ? turnoverData.toFixed(2) : '0.00' }}</span>
      </div>
      
      <!-- 导出按钮 -->
      <el-button 
        type="success" 
        icon="el-icon-download" 
        @click="exportToExcel"
        :disabled="!canExport"
      >
        导出为excel表格
      </el-button>
    </div>
    
    <!-- 分类标签页 -->
    <el-tabs v-model="activeCategoryId" @tab-click="handleCategoryChange">
      <el-tab-pane v-for="category in categories" :key="category.categoryId" :label="category.categoryName" :name="category.categoryId.toString()">
        <!-- 状态筛选选项 -->
        <div class="status-filter">
          <el-radio-group v-model="currentStatusFilter" @change="handleStatusFilterChange">
            <el-radio-button label="all">全部</el-radio-button>
            <el-radio-button label="unpaid">未收款</el-radio-button>
          </el-radio-group>
        </div>
        
        <!-- 加载状态提示 -->
        <div v-if="loading" class="loading-state">
          <el-icon><loading /></el-icon>
          <p>数据加载中，请稍候...</p>
        </div>
        
        <el-table
            v-else
            :data="filteredTableData"
            style="width: 100%"
            border
            stripe
            class="excel-table"
            ref="table"
            :row-height="50"
        >
          <!-- ID列 -->
          <el-table-column prop="id" label="序号" width="50">
            <template slot-scope="scope">
              <span class="id-label" :title="scope.row.id" :class="{ 'non-editable-text': !scope.row.editable }">{{ scope.row.id }}</span>
            </template>
          </el-table-column>
          
          <!-- 车牌号 -->
          <el-table-column prop="car" label="车号" width="100">
            <template slot-scope="scope">
              <el-autocomplete
                v-model="scope.row.car"
                :fetch-suggestions="(queryString, cb) => querySearch('car', queryString, cb)"
                placeholder="车号"
                size="small"
                :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                @select="(item) => handleCarSelect(scope.row, item)"
                @blur="handleCellBlur(scope.row, 'car')"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>
          
          <!-- 货物列 -->
          <el-table-column prop="name" label="货物" width="170">
            <template slot-scope="scope">
              <div class="input-with-clear">
                <el-autocomplete
                  v-model="scope.row.name"
                  :fetch-suggestions="(queryString, cb) => querySearch('name', queryString, cb)"
                  placeholder="输入货物"
                  size="small"
                  :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                  @select="(item) => handleProductSelect(scope.row, item)"
                  @blur="validateRow(scope.row)"
                  :class="{ 'non-editable-input': !scope.row.editable }"
                />
                <el-button
                  v-if="scope.row.editable && !scope.row.saving && !scope.row.deleting"
                  class="clear-button"
                  size="small"
                  circle
                  icon="el-icon-circle-close"
                  @click="clearRowData(scope.row)"
                ></el-button>
              </div>
            </template>
          </el-table-column>
          
          <!-- 数量列 -->
          <el-table-column label="数量" width="120">
            <template slot-scope="scope">
              <div class="quantity-with-unit">
                <el-input
                  v-model="scope.row.quantity"
                  placeholder="数量"
                  size="small"
                  :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                  @blur="validateRow(scope.row)"
                  :class="{ 'non-editable-input': !scope.row.editable }"
                />
                <span class="unit-label" :class="{ 'non-editable-text': !scope.row.editable }">{{ scope.row.unit || '单位' }}</span>
              </div>
            </template>
          </el-table-column>
          
          <!-- 价格列 -->
          <el-table-column prop="price" label="价格" width="100">
            <template slot-scope="scope">
              <el-autocomplete
                v-model="scope.row.price"
                :fetch-suggestions="(queryString, cb) => queryPriceSearch(scope.row.productId, queryString, cb)"
                placeholder="价格"
                size="small"
                :disabled="!scope.row.editable || !scope.row.productId || scope.row.saving || scope.row.deleting"
                @select="(item) => handlePriceSelect(scope.row, item)"
                @blur="handleCellBlur(scope.row, 'price')"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>
          
          <!-- 总价列 -->
          <el-table-column label="总价" width="100">
            <template slot-scope="scope">
              <el-input
                v-model="scope.row.totalPrice"
                size="small"
                :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                @blur="handleTotalPriceBlur(scope.row)"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>

          
          <!-- 地点列 -->
          <el-table-column prop="location" label="地点" width="200">
            <template slot-scope="scope">
              <el-autocomplete
                v-model="scope.row.location"
                :fetch-suggestions="(queryString, cb) => querySearch('location', queryString, cb)"
                placeholder="输入地点"
                size="small"
                :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                @select="(item) => handleLocationSelect(scope.row, item)"
                @blur="handleCellBlur(scope.row, 'location')"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>
          
          <!-- 挂单列 -->
          <el-table-column prop="order" label="挂单" width="100">
            <template slot-scope="scope">
              <el-autocomplete
                v-model="scope.row.order"
                :fetch-suggestions="(queryString, cb) => querySearch('order', queryString, cb)"
                placeholder="挂单"
                size="small"
                :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                @select="(item) => handleOrderSelect(scope.row, item)"
                @blur="handleCellBlur(scope.row, 'order')"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>
          
          <!-- 搬运列 -->
          <el-table-column prop="carry" label="搬运" width="100">
            <template slot-scope="scope">
              <el-autocomplete
                v-model="scope.row.carry"
                :fetch-suggestions="(queryString, cb) => querySearch('carry', queryString, cb)"
                placeholder="搬运"
                size="small"
                :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                @select="(item) => handleCarrySelect(scope.row, item)"
                @blur="handleCellBlur(scope.row, 'carry')"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>
          
          <!-- 时间列 -->
          <el-table-column prop="time" label="收款时间" width="135">
            <template slot-scope="scope">
              <el-time-picker
                v-model="scope.row.time"
                type="time"
                format="HH:mm:ss"
                value-format="HH:mm:ss"
                placeholder="选择时间"
                size="small"
                :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                @change="handleTimeChange(scope.row, $event)"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>
          
          <!-- 收款方式列 -->
          <el-table-column prop="methods" label="收款方式" width="175">
            <template slot-scope="scope">
              <el-autocomplete
                v-model="scope.row.methods"
                :fetch-suggestions="(queryString, cb) => querySearch('methods', queryString, cb)"
                placeholder="收款方式"
                size="small"
                :disabled="!scope.row.editable || scope.row.saving || scope.row.deleting"
                @select="(item) => handleMethodsSelect(scope.row, item)"
                @blur="handleCellBlur(scope.row, 'methods')"
                :class="{ 'non-editable-input': !scope.row.editable }"
              />
            </template>
          </el-table-column>
          
          <!-- 操作列 -->
          <el-table-column label="操作" width="153">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button 
                  v-if="scope.row.editable && tableData.length >= 1" 
                  size="small" 
                  type="danger" 
                  @click="deleteRow(scope.row)"
                  :disabled="scope.row.saving || scope.row.deleting"
                >
                  <el-icon v-if="scope.row.deleting" class="loading-icon"><loading /></el-icon>
                  <span v-else>删除</span>
                </el-button>
                
                <el-button 
                  v-if="!scope.row.editable" 
                  size="small" 
                  type="primary" 
                  @click="editRow(scope.row)"
                  :disabled="scope.row.saving || scope.row.deleting"
                >修改</el-button>
                
                <el-button 
                  v-if="scope.row.editable && scope.row.saved" 
                  size="small" 
                  type="success" 
                  @click="updateRow(scope.row)"
                  :disabled="scope.row.saving || scope.row.deleting"
                >
                  <el-icon v-if="scope.row.saving" class="loading-icon"><loading /></el-icon>
                  <span v-else>保存</span>
                </el-button>
              </div>
            </template>
          </el-table-column>
          
        </el-table>
        
        <div v-if="!loading" class="empty-state">
          <el-icon><plus /></el-icon>
          <el-button 
            type="primary" 
            @click="addNewRow"
            :disabled="!canAddNewRow"
            :class="{ 'is-disabled': !canAddNewRow }"
          >添加数据行</el-button>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import adminrequest from '@/utils/adminrequest'
import apirequest from '@/utils/apirequest'

export default {
  name: 'ExcelLikeTable',
  data() {
    return {
      tableData: [],
      timeout: null,
      nextId: 1,
      loading: false, // 加载状态
      // 搜索选项配置
      searchOptions: {
        car: [],
        name: [],
        owner: [],
        location: [],
        order: [],
        methods: [],
        carry: []
      },
      // 车牌与用户ID映射
      carUserMap: {},
      // 产品ID与产品信息映射
      productMap: {},
      // 产品ID与价格列表映射
      priceMap: {},
      // 地点ID与地点信息映射
      locationMap: {},
      // 挂单ID与挂单信息映射
      orderMap: {},
      // 搬运ID与搬运信息映射
      carryMap: {},
      // 单位配置
      units: ['件'],
      // 分类标签页相关
      categories: [],
      activeCategoryId: null,
      // 状态筛选 - "all"表示全部，"unpaid"表示未收款
      currentStatusFilter: 'all',
      // 日期选择器相关
      selectedDate: null,
      exportId: 0,
      // 场地选择器相关
      grounds: [],
      selectedGroundId: '0',
      // 总营业额相关
      turnoverData: null,
      turnoverLoading: false,
      turnoverError: false
    }
  },
  created() {
    // 初始化日期选择器为今天
    const today = new Date();
    this.selectedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // 并行获取场地数据和exportId
    Promise.all([
      this.fetchGrounds(),
      this.fetchExportId()
    ]).then(() => {
      this.loading = true;
      // 再获取分类数据
      this.fetchCategories().then(() => {
        // 如果有分类数据，默认选择第一个分类
        if (this.categories.length > 0) {
          this.activeCategoryId = this.categories[0].categoryId.toString();
          // 先加载产品数据，再加载表格数据
          this.fetchProducts().then(() => {
            this.loadTableData().finally(() => {
              this.loading = false;
              // 表格数据加载完成后获取营业额
              this.fetchTurnover();
            });
          }).catch(() => {
            this.loading = false;
          });
        } else {
          this.loading = false;
        }
        
        // 加载其他数据
        this.fetchCarNumbers();
        this.fetchOwners();
        this.fetchLocations();
        this.fetchOrders();
        this.fetchCarryInfo();
        this.fetchPaymentMethods();
      }).catch(() => {
        this.loading = false;
      });
    });
  },
  computed: {
    // 计算是否可以添加新行
    canAddNewRow() {
      // 如果没有数据行，允许添加
      if (this.tableData.length === 0) return true;
      
      // 获取最后一行
      const lastRow = this.tableData[this.tableData.length - 1];
      
      // 如果最后一行未处于编辑状态，允许添加
      if (!lastRow.editable) return true;
      
      // 如果最后一行已保存，允许添加
      if (lastRow.saved) return true;
      
      // 检查名称和数量是否都已填写
      return !!(lastRow.name && lastRow.quantity);
    },
    // 计算是否可以导出Excel
    canExport() {
      // 确保日期、exportId和分类ID都已设置
      return !!this.selectedDate && this.exportId > 0 && !!this.activeCategoryId;
    },
    // 根据当前筛选条件过滤表格数据
    filteredTableData() {
      // 如果筛选条件是"全部"，返回所有数据
      if (this.currentStatusFilter === 'all') {
        return this.tableData;
      }
      
      // 如果筛选条件是"未收款"，返回收款时间为null且收款方式为空的记录
      if (this.currentStatusFilter === 'unpaid') {
        return this.tableData.filter(row => {
          // 收款时间为null或空，且收款方式为空
          return (row.time === null || row.time === '') && (row.methods === null || row.methods === '');
        });
      }
      
      return this.tableData;
    }
  },
  methods: {
    // 获取总营业额数据
    fetchTurnover() {
      if (!this.exportId) {
        return;
      }
      
      this.turnoverLoading = true;
      this.turnoverError = false;
      
      const options = {
        method: 'GET',
        url: '/admin/export/getTurnover',
        params: {exportId: this.exportId}};
      
      adminrequest.request(options).then(response => {
        if (response.data.code === 200) {
          this.turnoverData = response.data.data;
        } else {
          this.turnoverError = true;
          console.error('获取营业额失败:', response.data.message);
        }
      }).catch(error => {
        this.turnoverError = true;
        console.error('获取营业额异常:', error);
      }).finally(() => {
        this.turnoverLoading = false;
      });
    },
    
    // 处理状态筛选变化
    handleStatusFilterChange() {
      // 当筛选条件变化时，重新编号
      this.renumberIds();
    },
    
    // 导出为Excel表格
    exportToExcel() {
      if (!this.canExport) {
        this.$message.warning('请确保已选择日期和分类');
        return;
      }
      
      try {
        const options = {
          method: 'GET',
          url: '/admin/export/getExcel',
          params: {
            date: this.selectedDate,
            exportId: this.exportId,
            categoryId: this.activeCategoryId
          },
          responseType: 'blob'
        };
    
        adminrequest.request(options).then(response => {
          // 处理文件下载
          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = `数据导出_${this.selectedDate}_分类${this.activeCategoryId}.xlsx`;
          document.body.appendChild(link);
          link.click();
          
          // 清理资源
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          this.$message.success('Excel导出成功');
        }).catch(error => {
          console.error('导出Excel失败:', error);
          this.$message.error('导出Excel失败，请稍后重试');
        });
      } catch (error) {
        console.error('导出Excel失败:', error);
        this.$message.error('导出Excel失败，请稍后重试');
      }
    },
    
    // 获取场地数据
    async fetchGrounds() {
      try {
        const options = {method: 'GET', url: '/api/select/getGround'};
        const response = await apirequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          this.grounds = response.data.data;
          console.log('场地数据加载成功');
          return Promise.resolve();
        } else {
          console.error('获取场地数据失败:', response.data.message);
          return Promise.reject();
        }
      } catch (error) {
        console.error('获取场地数据异常:', error);
        return Promise.reject();
      }
    },
    
    // 处理场地变更
    async handleGroundChange() {
      this.loading = true;
      
      try {
        // 重新获取exportId，因为groundId可能已改变
        await this.fetchExportId();
        
        // 如果有分类数据，重新加载表格数据
        if (this.activeCategoryId) {
          await this.loadTableData();
        }
        
        // 场地变更后重新获取营业额
        this.fetchTurnover();
        
        this.$message.success('场地切换成功');
      } catch (error) {
        this.$message.error('场地切换失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 根据日期和场地获取exportId
    async fetchExportId() {
      if (!this.selectedDate) {
        return;
      }
      
      try {
        const options = {
          method: 'GET',
          url: '/admin/export/getListId',
          params: {exportDate: this.selectedDate, groundId: this.selectedGroundId}
        };
        
        const response = await adminrequest.request(options);
        
        if (response.data.code === 200 && response.data.data !== undefined) {
          this.exportId = response.data.data;
          console.log(`获取exportId成功: ${this.exportId}`);
          return Promise.resolve();
        } else {
          console.error('获取exportId失败:', response.data.message);
          this.$message.error('获取exportId失败');
          return Promise.reject();
        }
      } catch (error) {
        console.error('获取exportId异常:', error);
        this.$message.error('获取exportId异常，请稍后重试');
        return Promise.reject(error);
      }
    },
    
    // 处理日期变更
    async handleDateChange() {
      this.loading = true;
      
      try {
        // 重新获取exportId
        await this.fetchExportId();
        
        // 如果有分类数据，重新加载表格数据
        if (this.activeCategoryId) {
          await this.loadTableData();
        }
        
        // 日期变更后重新获取营业额
        this.fetchTurnover();
        
        this.$message.success('日期切换成功');
      } catch (error) {
        this.$message.error('日期切换失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 获取分类数据
    async fetchCategories() {
      try {
        const options = {method: 'GET', url: '/api/select/getCategory'};
        const response = await apirequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          this.categories = response.data.data;
          console.log('分类数据加载成功');
          return Promise.resolve();
        } else {
          console.error('获取分类数据失败:', response.data.message);
          return Promise.reject();
        }
      } catch (error) {
        console.error('获取分类数据异常:', error);
        return Promise.reject();
      }
    },
    
    // 处理分类切换
    async handleCategoryChange(tab) {
      this.activeCategoryId = tab.name;
      this.loading = true;
      
      try {
        await this.fetchProducts();
        await this.loadTableData();
        this.currentStatusFilter = 'all';
        console.log(`成功切换到分类: ${this.activeCategoryId}`);
      } catch (error) {
        console.error('切换分类时发生错误:', error);
        this.$message.error('切换分类失败，请重试');
      } finally {
        this.loading = false;
      }
    },
    
    // 加载表格数据
    async loadTableData() {
      // 验证必要参数
      if (!this.exportId || !this.activeCategoryId) {
        console.error('缺少必要参数: exportId或categoryId');
        this.tableData = [];
        return;
      }
      
      try {
        const options = {
          method: 'GET',
          url: '/admin/export/getExportItem',
          params: {
            exportId: this.exportId, 
            categoryId: this.activeCategoryId
          }
        };
        
        console.log('请求表格数据:', options);
        const response = await adminrequest.request(options);
        
        if (response.data.code === 200) {
          // 确保数据是数组
          const data = Array.isArray(response.data.data) ? response.data.data : [];
          
          // 转换API数据格式为表格所需格式
          this.tableData = data.map((item, index) => ({
            id: index + 1,
            itemId: item.itemId || null,
            car: item.carNumber || '',
            userId: item.driverId || null,
            name: item.productName ? `${item.productName}(${item.productUnit || '件'})` : '',
            productId: item.productId || null,
            quantity: item.quantity !== undefined ? item.quantity.toString() : '',
            price: item.price ? item.price.toString() : '',
            priceId: null,
            totalPrice: item.totalPrice ? item.totalPrice.toString() : '',
            owner: item.ownerName || '',
            location: item.addr || '',
            locationId: null,
            order: item.payeeName || '',
            orderId: null,
            time: item.paymentTime || null,
            carry: item.carry || '',
            carryId: null,
            methods: item.paymentMethods || '',
            unit: item.productUnit || '件',
            editable: false,
            saved: true,
            allowEditTotal: false,
            saving: false, // 行内保存状态
            deleting: false // 行内删除状态
          }));
          
          // 更新下一个ID
          this.nextId = this.tableData.length > 0 ? this.tableData[this.tableData.length - 1].id + 1 : 1;
          
          console.log(`表格数据加载成功，共${this.tableData.length}条记录`);
        } else {
          console.error('获取表格数据失败:', response.data.message);
          this.tableData = [];
          this.$message.error('获取表格数据失败');
        }
      } catch (error) {
        console.error('获取表格数据异常:', error);
        this.tableData = [];
        this.$message.error('获取表格数据异常');
        throw error;
      }
    },
    
    // 获取车牌数据
    async fetchCarNumbers() {
      try {
        const options = {method: 'GET', url: '/admin/export/getCarNumber'};
        const response = await adminrequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          // 提取车牌数据
          this.searchOptions.car = response.data.data.map(item => item.carNumber);
          
          // 建立车牌与userID的映射
          response.data.data.forEach(item => {
            this.carUserMap[item.carNumber] = item.userId;
          });
          
          console.log('车牌数据加载成功');
        } else {
          console.error('获取车牌数据失败:', response.data.message);
        }
      } catch (error) {
        console.error('获取车牌数据异常:', error);
       }
    },
    
    // 获取产品数据
    async fetchProducts() {
      try {
        const options = {
          method: 'GET',
          url: '/api/select/getProduct',
          params: { categoryId: this.activeCategoryId }
        };
        const response = await apirequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          // 生成搜索选项：保留 "名称(单位)" 格式
          this.searchOptions.name = response.data.data.map(item => {
            return `${item.productName}(${item.productUnit})`;
          });
          
          // 构建 productMap：用 "名称-单位" 作为唯一键
          response.data.data.forEach(item => {
            const uniqueKey = `${item.productName}-${item.productUnit}`;
            this.productMap[uniqueKey] = item;
          });
          
          console.log('产品数据加载成功');
        }
      } catch (error) {
        console.error('获取产品数据异常:', error);
        throw error;
      }
    },
    
    // 获取货主姓名数据
    async fetchOwners() {
      try {
        const options = {method: 'GET', url: '/api/select/getOwner'};
        const response = await apirequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          // 提取货主姓名
          this.searchOptions.owner = response.data.data.map(item => item.ownerName);
          
          console.log('货主姓名数据加载成功');
        } else {
          console.error('获取货主姓名数据失败:', response.data.message);
        }
      } catch (error) {
        console.error('获取货主姓名数据异常:', error);
		throw error;
      }
    },
    
    // 获取地点数据
    async fetchLocations() {
      try {
        const options = {method: 'GET', url: '/api/select/getAddr'};
        const response = await apirequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          // 提取地点名称
          this.searchOptions.location = response.data.data.map(item => item.addr);
          
          // 建立地点ID与地点信息的映射
          response.data.data.forEach(item => {
            this.locationMap[item.addr] = item;
          });
          
          console.log('地点数据加载成功');
        } else {
          console.error('获取地点数据失败:', response.data.message);
        }
      } catch (error) {
        console.error('获取地点数据异常:', error);
		throw error;
      }
    },
    
    // 获取挂单数据
    async fetchOrders() {
      try {
        const options = {method: 'GET', url: '/admin/export/getPayee'};
        const response = await adminrequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          // 提取挂单名称
          this.searchOptions.order = response.data.data.map(item => item.payeeName);
          
          // 建立挂单ID与挂单信息的映射
          response.data.data.forEach(item => {
            this.orderMap[item.payeeName] = item;
          });
          
          console.log('挂单数据加载成功');
        } else {
          console.error('获取挂单数据失败:', response.data.message);
        }
      } catch (error) {
        console.error('获取挂单数据异常:', error);
		throw error;
      }
    },
    
    // 获取搬运信息
    async fetchCarryInfo() {
      try {
        const options = {method: 'GET', url: '/admin/export/getCarry'};
        const response = await adminrequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          // 提取搬运信息
          this.searchOptions.carry = response.data.data.map(item => item.carryName);
          
          // 建立搬运ID与搬运信息的映射
          response.data.data.forEach(item => {
            this.carryMap[item.carryName] = item;
          });
          
          console.log('搬运信息加载成功');
        } else {
          console.error('获取搬运信息失败:', response.data.message);
        }
      } catch (error) {
        console.error('获取搬运信息异常:', error);
		throw error;
      }
    },
    
    // 获取收款方式
    fetchPaymentMethods() {
      // 直接定义固定的收款方式选项
      this.searchOptions.methods = ['现金', '微信支付', '支付宝', '大炳', '勇', '环', '银行卡'];
    },
    
    // 根据产品ID获取价格列表
    async fetchPricesByProductId(productId) {
      if (!productId || this.priceMap[productId]) {
        return;
      }
      
      try {
        const options = {
          method: 'GET',
          url: '/admin/export/getPrice',
          params: {productId: productId}
        };
        
        const response = await adminrequest.request(options);
        
        if (response.data.code === 200 && response.data.data) {
          // 提取价格数据
          const prices = response.data.data.map(item => ({
            value: item.price.toString(),
            priceId: item.priceId
          }));
          
          // 建立产品ID与价格列表的映射
          this.priceMap[productId] = prices;
          
          console.log(`产品ID ${productId} 的价格数据加载成功`);
        } else {
          console.error(`获取产品ID ${productId} 的价格数据失败:`, response.data.message);
          this.priceMap[productId] = [];
        }
      } catch (error) {
        console.error(`获取产品ID ${productId} 的价格数据异常:`, error);
        this.priceMap[productId] = [];
      }
    },
    
    // 添加新行
    async addNewRow() {
      // 如果有未保存的行，先保存
      const unsavedRow = this.tableData.find(row => row.editable && !row.saved);
      if (unsavedRow) {
        await this.saveRow(unsavedRow);
      }
      
      const newRow = {
        id: this.nextId++,
        itemId: null,
        car: '',
        userId: null,
        name: '',
        productId: null,
        quantity: '',
        price: '',
        priceId: null,
        totalPrice: '',
        owner: '',
        location: '',
        locationId: null,
        order: '',
        orderId: null,
        time: null,
        carry: '',
        carryId: null,
        methods: '',
        unit: this.units[0],
        editable: true,
        saved: false,
        allowEditTotal: false,
        saving: false,
        deleting: false
      };
      
      this.tableData.push(newRow);
      
      // 自动聚焦到新行的第一个输入框
      this.$nextTick(() => {
        const inputs = this.$refs.table.$el.querySelectorAll('.el-input__inner');
        if (inputs.length > 0) inputs[inputs.length - 1].focus();
      });
    },
    
    // 搜索建议
    querySearch(prop, queryString, cb) {
      const options = this.searchOptions[prop] || [];
      const results = queryString 
        ? options.filter(option => 
            option.toLowerCase().includes(queryString.toLowerCase())
          )
        : options;
      
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        cb(results.map(option => ({ value: option })));
      }, 300);
    },
    
    // 搜索价格建议
    async queryPriceSearch(productId, queryString, cb) {
      if (!productId) {
        cb([]);
        return;
      }
      
      // 如果价格数据还未加载，则先加载
      if (!this.priceMap[productId]) {
        await this.fetchPricesByProductId(productId);
      }
      
      const prices = this.priceMap[productId] || [];
      const results = queryString 
        ? prices.filter(price => 
            price.value.includes(queryString)
          )
        : prices;
      
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        cb(results);
      }, 300);
    },
    
    // 处理车牌选择
    handleCarSelect(row, item) {
      row.car = item.value;
      // 根据车牌获取对应的userId
      row.userId = this.carUserMap[item.value] || null;
    },
    
    // 处理产品选择
    async handleProductSelect(row, item) {
      // 从选择的文本中提取名称和单位
      const match = item.value.match(/^([^()]+)\(([^)]+)\)$/);
      if (!match) {
        this.$message.warning('产品格式错误，请选择下拉列表中的选项');
        return;
      }
      const [_, productName, productUnit] = match;
      
      // 用 "名称-单位" 作为唯一键匹配产品
      const uniqueKey = `${productName}-${productUnit}`;
      const product = this.productMap[uniqueKey];
      
      if (product) {
        row.name = item.value;
        row.productId = product.productId;
        row.unit = product.productUnit;
        
        // 清空原有价格信息，重新加载该产品的价格
        row.price = '';
        row.priceId = null;
        row.totalPrice = '';
        await this.fetchPricesByProductId(product.productId);
      } else {
        this.$message.warning('未找到对应产品，请重试');
        row.name = '';
        row.productId = null;
        row.unit = this.units[0];
      }
    },
    
    // 处理价格选择
    handlePriceSelect(row, item) {
      row.price = item.value;
      row.priceId = item.priceId;
      
      // 立即计算总价
      this.calculateTotalPrice(row);
    },
    
    // 处理地点选择
    handleLocationSelect(row, item) {
      row.location = item.value;
      // 根据地点获取对应的locationId
      row.locationId = this.locationMap[item.value]?.addrId || null;
    },
    
    // 处理挂单选择
    handleOrderSelect(row, item) {
      row.order = item.value;
      // 根据挂单获取对应的orderId
      row.orderId = this.orderMap[item.value]?.payeeId || null;
    },
    
    // 处理搬运选择
    handleCarrySelect(row, item) {
      row.carry = item.value;
      // 根据搬运信息获取对应的carryId
      row.carryId = this.carryMap[item.value]?.carryId || null;
    },
    
    // 处理收款方式选择
    handleMethodsSelect(row, item) {
      row.methods = item.value;
    },
    
    // 处理其他选择项
    handleSelect(prop, row, item) {
      row[prop] = item.value;
    },
    
    // 处理时间选择变化
    handleTimeChange(row, value) {
      // 当用户清除选择时，设置为null
      row.time = value || null;
    },
    
    renumberIds() {
      // 根据筛选后的数据重新编号
      this.filteredTableData.forEach((row, index) => {
        row.id = index + 1;
      });
      this.nextId = this.filteredTableData.length + 1;
    },
    
    // 删除行
    async deleteRow(row) {
      this.$confirm('确定要删除这条记录吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        row.deleting = true;
        const originalIndex = this.tableData.indexOf(row);
        
        // 本地先删除行
        this.deleteRowFromTable(row);
        
        try {
          if (row.itemId) { // 仅已保存的行需要调用接口删除
            const options = {
              method: 'DELETE',
              url: '/admin/export/deleteExportItem',
              params: {itemId: row.itemId}
            };
            const response = await adminrequest.request(options);
            
            if (response.data.code !== 200 || response.data.data !== 1) {
              // 接口失败：恢复本地数据
              this.tableData.splice(originalIndex, 0, row);
              this.renumberIds();
              this.$message.error(`删除失败: ${response.data.message}`);
            } else {
              this.$message.success('删除成功');
              this.fetchTurnover();
            }
          }
        } catch (error) {
          // 异常：恢复本地数据
          this.tableData.splice(originalIndex, 0, row);
          this.renumberIds();
          console.error('删除异常:', error);
          this.$message.error('删除异常，请重试');
        } finally {
          row.deleting = false;
        }
      }).catch(() => {});
    },
    
    // 从表格中删除行
    deleteRowFromTable(row) {
      const index = this.tableData.indexOf(row);
      if (index > -1) {
        this.tableData.splice(index, 1);
        this.renumberIds();
      }
    },
    
    // 编辑行
    async editRow(row) {
      // 如果有未保存的行，先保存
      const unsavedRow = this.tableData.find(r => r.editable && !r.saved);
      if (unsavedRow) {
        await this.saveRow(unsavedRow);
      }
      
      row.editable = true;
    },
    
    // 保存单行数据
    async saveRow(row) {
      // 先进行数据验证
      if (!this.validateRowData(row)) {
        return;
      }
      
      row.saving = true;
      
      // 构建要发送的数据
      const dataToSave = {
        itemId: row.itemId,
        exportId: this.exportId,
        categoryId: this.activeCategoryId,
        driverId: row.userId || null,
        productId: row.productId || null,
        price: parseFloat(row.price) || null,
        addr: row.location || '',
        quantity: parseFloat(row.quantity),
        totalPrice: parseFloat(row.totalPrice) || null,
        carry: row.carry || '',
        paymentTime: row.time || null,
        paymentMethods: row.methods || '',
        ownerName: row.owner || '',
        payeeName: row.order || ''
      };
      
      try {
        let response;
        if (row.itemId) {
          // 更新现有记录
          const options = {
            method: 'POST',
            url: '/admin/export/updateExportItem',
            headers: {'content-type': 'application/json'},
            data: dataToSave
          };
          
          response = await adminrequest.request(options);
        } else {
          // 新增记录
          const options = {
            method: 'POST',
            url: '/admin/export/addExportItem',
            headers: {'content-type': 'application/json'},
            data: dataToSave
          };
          
          response = await adminrequest.request(options);
          
          // 如果新增成功，更新itemId
          if (response.data.code === 200 && response.data.data) {
            row.itemId = response.data.data;
          }
        }
        
        if (response.data.code === 200) {
          row.saved = true;
          row.editable = false;
          this.$message.success('数据保存成功');
          this.fetchTurnover();
        } else {
          this.$message.error(`数据保存失败: ${response.data.message}`);
        }
      } catch (error) {
        console.error('保存数据异常:', error);
        this.$message.error('保存数据异常，请稍后重试');
      } finally {
        row.saving = false;
      }
    },
    
    // 更新行数据
    async updateRow(row) {
      await this.saveRow(row);
    },
    
    // 验证行数据
    validateRowData(row) {
      // 检查必要字段
      if (!row.name) {
        this.$message.warning('请选择货物名称');
        return false;
      }
      
      if (!row.quantity || isNaN(parseFloat(row.quantity))) {
        this.$message.warning('请输入有效的数量');
        return false;
      }
      
      if (parseFloat(row.quantity) <= 0) {
        this.$message.warning('数量必须大于0');
        return false;
      }
      
      return true;
    },
    
    // 处理单元格失焦事件
    handleCellBlur(row, prop) {
      // 计算总价
      if ((prop === 'price' || prop === 'quantity') && !row.ignoreAutoCalc) {
        this.calculateTotalPrice(row);
      }
    },
    
    // 处理总价失焦事件
    handleTotalPriceBlur(row) {
      // 如果总价被手动修改，重新计算单价
      const price = parseFloat(row.price) || 0;
      const quantity = parseFloat(row.quantity) || 0;
      const autoTotalPrice = (price * quantity).toFixed(2);
      const manualTotalPrice = parseFloat(row.totalPrice);
      
      if (!isNaN(manualTotalPrice) && !isNaN(quantity) && quantity > 0 && manualTotalPrice !== parseFloat(autoTotalPrice)) {
        // 防止循环计算
        row.ignoreAutoCalc = true;
        row.price = (manualTotalPrice / quantity).toFixed(2);
        // 重新计算总价以确保显示一致
        row.totalPrice = manualTotalPrice.toFixed(2);
        // 允许下次自动计算
        this.$nextTick(() => {
          row.ignoreAutoCalc = false;
        });
      }
    },
    
    // 验证行数据
    validateRow(row) {
      // 检查名称和数量是否都已填写
      const isValid = !!(row.name && row.quantity);
      
      // 如果行有效且总价未计算，则计算总价
      if (isValid && !row.totalPrice) {
        this.calculateTotalPrice(row);
      }
    },
    
    // 清空行数据
    clearRowData(row) {
      row.name = '';
      row.productId = null;
      row.quantity = '';
      row.price = '';
      row.priceId = null;
      row.totalPrice = '';
      row.time = null;
      
      // 自动聚焦到货物输入框
      this.$nextTick(() => {
        const inputs = this.$refs.table.$el.querySelectorAll('.el-input__inner');
        if (inputs.length > 0) inputs[inputs.length - 1].focus();
      });
    },
    
    // 计算总价
    calculateTotalPrice(row) {
      const price = parseFloat(row.price) || 0;
      const quantity = parseFloat(row.quantity) || 0;
      
      if (price > 0 && quantity > 0) {
        row.totalPrice = (price * quantity).toFixed(2);
      }
    }
  }
}
</script>

<style scoped>
	
/* 表格整体容器样式 */
.excel-like-table {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
}

/* 表格头部样式 */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
}

/* 总营业额显示样式 */
.turnover-display {
  margin: 0 10px;
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

/* 筛选栏样式 */
.filter-bar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

/* 状态筛选样式 */
.status-filter {
  margin-bottom: 15px;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

/* 标签页样式调整 */
.el-tabs {
  margin-bottom: 20px;
}

.el-tabs__item {
  font-size: 16px;
  font-weight: 500;
  padding: 0 20px;
  height: 40px;
  line-height: 40px;
}

.el-tabs__active-bar {
  height: 3px;
  background-color: #409EFF;
}

.el-tabs__item.is-active {
  color: #409EFF;
}

/* 加载状态样式 */
.loading-state {
  text-align: center;
  padding: 30px 0;
  color: #909399;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.loading-state .el-icon {
  font-size: 36px;
  margin-bottom: 15px;
  color: #409EFF;
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 表格内输入框样式 */
.excel-table .el-input {
  width: 100%;
  margin: 0;
}

/* 空状态提示样式 */
.empty-state {
  text-align: center;
  padding: 30px 0;
  color: #909399;
}

.empty-state .el-icon {
  font-size: 36px;
  margin-bottom: 15px;
  color: #409EFF;
}

.empty-state p {
  margin-bottom: 15px;
}

/* 操作按钮容器 */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 表格行高 */
.excel-table .el-table__row {
  height: 50px;
}

/* 表格单元格 */
.excel-table .el-table__cell {
  padding: 0 8px;
  vertical-align: middle;
}

/* 按钮样式 */
.excel-table .el-button {
  height: 32px;
  padding: 0 10px;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

/* 数量和单位容器 */
.quantity-with-unit {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 单位标签 */
.unit-label {
  min-width: 30px;
  text-align: center;
  color: #606266;
  font-size: 14px;
}

/* 按钮交互效果 */
.excel-table .el-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.excel-table .el-button:active {
  transform: translateY(0);
}

/* 保存按钮样式 */
.el-button--success {
  background-color: #67c23a;
  border-color: #67c23a;
}

.el-button--success:hover {
  background-color: #57b22a;
  border-color: #57b22a;
}

/* 删除按钮样式 */
.el-button--danger {
  background-color: #f56c6c;
  border-color: #f56c6c;
}

.el-button--danger:hover {
  background-color: #e85050;
  border-color: #e85050;
}

/* 编辑按钮样式 */
.el-button--primary {
  background-color: #409EFF;
  border-color: #409EFF;
}

.el-button--primary:hover {
  background-color: #337ecc;
  border-color: #337ecc;
}

/* 禁用按钮样式 */
.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* 输入框和清空按钮容器 */
.input-with-clear {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 清空按钮样式 */
.clear-button {
  height: 28px;
  width: 28px;
  padding: 0;
  background-color: #f56c6c;
  border-color: #f56c6c;
  color: white;
  opacity: 0.8;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-button:hover {
  opacity: 1;
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.3);
}

.clear-button:active {
  transform: scale(0.95);
}

.clear-button .el-icon {
  font-size: 14px;
}

/* 加载图标样式 */
.loading-icon {
  animation: rotate 2s linear infinite;
  font-size: 16px;
}

/* 表格最小高度，避免加载时闪烁 */
.excel-table {
  min-height: 300px;
}

/* 不可编辑状态文本样式 */
.non-editable-text {
  color: #000000;
  font-weight: normal;
  opacity: 0.8;
}

/* 不可编辑状态输入框文本样式 */
::v-deep .non-editable-input .el-input__inner {
  color: #000000 !important;
  font-weight: normal !important;
  opacity: 0.9 !important;
  background-color: #f5f7fa !important;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .turnover-display {
    margin: 10px 0;
  }
}
</style>
